import { Option, Select, Sheet, Stack, Table, Tooltip, Typography } from '@healthinal/ui';
import { ReactNode } from 'react';
import { ImportCandidateDto, ImportColumn, ImportPreviewRowDto } from '../../../api/created/importFile.ts';
import { TruncateText } from '../../../components/TruncateText.tsx';
import { formatDate, formatDateWithGermanMonth } from '../../../utils/date.ts';
import { getGenderName } from '../../../utils/gender.ts';
import { toDisplayRowNumber } from '../../../utils/importSubmitCode.ts';
import { getImportTargetColumns, getNoteColumnLabel, ImportTarget } from './importTarget.ts';
import {
  getAvailableActions,
  getLinkableCandidates,
  isLinkAction,
  ROW_DECISION_LABELS,
  RowDecision,
  RowDecisions,
  withDefaultPerson,
} from './rowDecision.ts';

// Columns without a width share the remaining space. Below the table's min width it scrolls horizontally.
const COLUMNS: {
  column: ImportColumn;
  label: string;
  width?: number;
  render: (row: ImportPreviewRowDto) => ReactNode;
}[] = [
  {
    column: 'GENDER',
    label: 'Geschlecht',
    width: 100,
    render: ({ values }) => values.gender && getGenderName(values.gender),
  },
  { column: 'FIRST_NAME', label: 'Vorname', render: ({ values }) => values.firstName },
  { column: 'LAST_NAME', label: 'Nachname', render: ({ values }) => values.lastName },
  {
    column: 'BIRTH_DATE',
    label: 'Geburtsdatum',
    width: 120,
    render: ({ values }) => formatDateWithGermanMonth(values.birthDate),
  },
  { column: 'STREET', label: 'Strasse', render: ({ values }) => values.street },
  { column: 'POSTAL_CODE', label: 'PLZ', width: 70, render: ({ values }) => values.postalCode },
  { column: 'CITY', label: 'Ort', render: ({ values }) => values.city },
  { column: 'NATIONALITY', label: 'Nationalität', render: ({ values }) => values.nationality?.name },
  { column: 'ASYLUM_NUMBER', label: 'N-Nummer', width: 110, render: ({ values }) => values.asylumNumber },
  { column: 'NOTE', label: 'Bemerkungen', render: ({ values }) => values.note },
  {
    column: 'LAST_CONTACT_DATE',
    label: 'Datum letzter Kontakt',
    width: 120,
    render: ({ values }) => formatDateWithGermanMonth(values.lastContactDate),
  },
];

const TABLE_MIN_WIDTH = 1300;

interface Props {
  targetType: ImportTarget['type'];
  rows: ImportPreviewRowDto[];
  decisions: RowDecisions;
  onDecisionChange: (rowNumber: number, decision: RowDecision) => void;
}

export function ImportReviewTable({ targetType, rows, decisions, onDecisionChange }: Props) {
  // Only the columns of the target's import template.
  const targetColumns = getImportTargetColumns(targetType);
  const columns = COLUMNS.filter(({ column }) => targetColumns.includes(column)).map((column) =>
    column.column === 'NOTE' ? { ...column, label: getNoteColumnLabel(targetType) } : column,
  );

  return (
    <Sheet sx={{ overflow: 'auto', width: '100%' }}>
      <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%', minWidth: TABLE_MIN_WIDTH }}>
        <thead>
          <tr>
            <th style={{ width: 60 }}>
              <TruncateText>Zeile</TruncateText>
            </th>
            {columns.map(({ column, label, width }) => (
              <th key={column} style={width ? { width } : undefined}>
                <TruncateText>{label}</TruncateText>
              </th>
            ))}
            <th style={{ width: 240 }}>
              <TruncateText>Aktion</TruncateText>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.rowNumber}>
              <td>
                <TruncateText>{toDisplayRowNumber(row.rowNumber)}</TruncateText>
              </td>
              {columns.map(({ column, render }) => (
                <ReviewCell key={column} row={row} column={column}>
                  {render(row)}
                </ReviewCell>
              ))}
              <td>
                {decisions[row.rowNumber] && (
                  <DecisionCell
                    row={row}
                    decision={decisions[row.rowNumber]}
                    onChange={(decision) => onDecisionChange(row.rowNumber, decision)}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Sheet>
  );
}

// Long values are cut off with an ellipsis and shown in a tooltip. Cells with an error show the
// value from the file and always explain the error in the tooltip. Cells matching an existing
// person are marked red for a full match and yellow for a partial one.
function ReviewCell({
  row,
  column,
  children,
}: {
  row: ImportPreviewRowDto;
  column: ImportColumn;
  children: ReactNode;
}) {
  const error = row.errors.find((cellError) => cellError.column === column);
  if (error) {
    return (
      <td>
        <Tooltip
          variant={'outlined'}
          color={'danger'}
          title={error.value ? `${error.value}: ${error.message}` : error.message}>
          <Typography color={'danger'} fontWeight={'lg'} noWrap>
            {error.value ?? '–'}
          </Typography>
        </Tooltip>
      </td>
    );
  }

  const { matchLevel, matchedFields } = row.duplicate;
  const isMatched = matchLevel !== 'NONE' && matchedFields.includes(column);
  return (
    <td
      style={
        isMatched
          ? {
              backgroundColor:
                matchLevel === 'FULL' ? 'var(--joy-palette-danger-softBg)' : 'var(--joy-palette-warning-softBg)',
            }
          : undefined
      }>
      <TruncateText>{children}</TruncateText>
    </td>
  );
}

function DecisionCell({
  row,
  decision,
  onChange,
}: {
  row: ImportPreviewRowDto;
  decision: RowDecision;
  onChange: (decision: RowDecision) => void;
}) {
  const availableActions = getAvailableActions(row);
  const candidates = getLinkableCandidates(row);

  return (
    <Stack gap={1}>
      <Select
        size={'sm'}
        value={decision.action}
        disabled={availableActions.length === 1}
        renderValue={renderTruncatedValue}
        slotProps={{ listbox: { sx: { maxWidth: 480 } } }}
        onChange={(_, action) => action && onChange(withDefaultPerson(row, { action, personId: decision.personId }))}>
        {availableActions.map((action) => (
          <Option key={action} value={action}>
            {ROW_DECISION_LABELS[action]}
          </Option>
        ))}
      </Select>
      {isLinkAction(decision.action) && candidates.length > 1 && (
        <Select
          size={'sm'}
          value={decision.personId}
          renderValue={renderTruncatedValue}
          onChange={(_, personId) => personId && onChange({ ...decision, personId })}>
          {candidates.map((candidate) => (
            <Option key={candidate.person.id} value={candidate.person.id}>
              {getCandidateLabel(candidate)}
            </Option>
          ))}
        </Select>
      )}
    </Stack>
  );
}

function getCandidateLabel({ person }: ImportCandidateDto) {
  return [`${person.lastName} ${person.firstName}`, formatDate(person.birthDate), person.asylumNumber]
    .filter(Boolean)
    .join(', ');
}

function renderTruncatedValue(option: { label: ReactNode } | null) {
  return option ? <TruncateText>{option.label}</TruncateText> : null;
}
